import React, { useState, useEffect } from "react";
import GuestDetailsModal from "../../Modals/guestDetailsModal/GuestDetailsModal";
import "./GuestList.css";
import { PhoneIcon, RsvpIcon, DropdownArrowIcon } from "../../helper/icon";

const GuestList = ({ guests, event, onRefresh, userRole, invitation }) => {
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [checkInFilter, setCheckInFilter] = useState("all");

  useEffect(() => {
    if (selectedGuest) {
      setIsModalOpen(true);
    }
  }, [selectedGuest]);

  const isUsher = userRole?.includes("usher");

  const filteredGuests = guests.filter((guest) => {
    if (checkInFilter === "checked-in") {
      return guest.isCheckedIn === true;
    } else if (checkInFilter === "not-checked-in") {
      return guest.isCheckedIn !== true;
    }
    return true;
  });

  const indexOfLastGuest = currentPage * rowsPerPage;
  const indexOfFirstGuest = indexOfLastGuest - rowsPerPage;
  const currentGuests = filteredGuests.slice(
    indexOfFirstGuest,
    indexOfLastGuest
  );

  const handlePageChange = (direction) => {
    if (direction === "prev" && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    } else if (
      direction === "next" &&
      indexOfLastGuest < filteredGuests.length
    ) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleCheckInFilterChange = (filter) => {
    setCheckInFilter(filter);
    setCurrentPage(1);
  };

  const handleGuestClick = (guest) => {
    const matchedInvitation = invitation.find(
      (inv) => inv.guestId?._id === guest.id
    );
    if (!matchedInvitation) {
      // console.warn("No matching invitation found for guest", guest);
      return;
    }

    const cashGift = matchedInvitation?.cashGiftPaid || {};
    const cashGiftDescription = cashGift.giftdescription || "";

    const normalizedGuestDetails = {
      guestDetails: {
        firstName: guest.firstName || guest.name?.split(" ")[0],
        lastName: guest.lastName || guest.name?.split(" ").slice(1).join(" "),
        email: guest.email,
        phoneNumber: guest.phone,
        profileImage: guest.userPhoto,
      },

      eventDetails: {
        eventTitle: event?.eventTitle,
        eventDate:
          event?.eventDate ||
          event?.startDate ||
          event?.multipleDates?.[0]?.date,

        eventAttire: {
          refundDeadlineDate: event?.eventAttire?.refundDeadlineDate || null,
        },
      },
      paymentDetails: {
        currency: event?.preferredCurrency,
        eventAttire:
          matchedInvitation?.eventAttire?.map((a) => ({
            attireTag: a.attireTag,
            price: a.price,
            quantity: a.quantity,
            totalAmount: a.totalAmount,
            uri: a.uri,
            paid: a.paid,
            paymentDate: a.paymentDate,
          })) || [],

        deliveryAddress: matchedInvitation?.eventAttire?.[0]?.deliveryAddress,
        cashGiftAmountPaid:
          matchedInvitation?.cashGiftPaid?.giftAmountPaid || 0,
        cashGiftDescription,
        ticketType: guest.ticket,
        ticketFeePaid: guest.ticketFeePaid[0]?.totalAmount || 0,
      },

      isCheckedIn: guest.isCheckedIn,
      checkInTime: guest.checkInTime,
    };

    setSelectedGuest(normalizedGuestDetails);
  };

  const handleDecline = () => {};
  const handleApprove = () => {};

  const checkedInCount = guests.filter((g) => g.isCheckedIn).length;
  const notCheckedInCount = guests.length - checkedInCount;

  return (
    <div className="guest-list-container">
      <div className="guest-list-checkin-filter-tabs">
        <button
          className={`guest-list-filter-tab ${
            checkInFilter === "all" ? "guest-list-active" : ""
          }`}
          onClick={() => handleCheckInFilterChange("all")}
        >
          All Guests
          <span className="guest-list-filter-badge">{guests.length}</span>
        </button>
        <button
          className={`guest-list-filter-tab ${
            checkInFilter === "checked-in" ? "guest-list-active" : ""
          }`}
          onClick={() => handleCheckInFilterChange("checked-in")}
        >
          Checked In
          <span className="guest-list-filter-badge guest-list-checked-in">
            {checkedInCount}
          </span>
        </button>
        <button
          className={`guest-list-filter-tab ${
            checkInFilter === "not-checked-in" ? "guest-list-active" : ""
          }`}
          onClick={() => handleCheckInFilterChange("not-checked-in")}
        >
          Not Checked In
          <span className="guest-list-filter-badge guest-list-not-checked-in">
            {notCheckedInCount}
          </span>
        </button>
      </div>

      <div className="guest-list-list">
        {currentGuests.length === 0 ? (
          <div className="guest-list-no-guests-message">
            {checkInFilter === "all"
              ? "No guests found."
              : checkInFilter === "checked-in"
              ? "No guests have checked in yet."
              : "All guests have checked in."}
          </div>
        ) : (
          currentGuests.map((guest) => (
            <div
              key={guest.id}
              className="guest-list-item"
              onClick={() => handleGuestClick(guest)}
              style={{ cursor: "pointer" }}
            >
              <div className="guest-list-info">
                <div className="guest-list-avatar">
                  <img
                    src={guest.userPhoto}
                    alt={guest.name || "Guest"}
                    onError={(e) => (e.target.src = "/images/avatar.webp")}
                  />
                  {guest.isCheckedIn && (
                    <div className="guest-list-checkin-indicator">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"
                          fill="white"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="guest-list-details">
                  <h3>{guest.name || "Unknown Guest"}</h3>
                  <p className="guest-list-email">{guest.email}</p>
                  <p className="guest-list-phone">
                    <PhoneIcon />
                    {guest.phone}
                  </p>
                </div>
              </div>

              <div className="guest-list-actions">
                <div className="guest-list-status-row">
                  {guest.rsvp && (
                    <button className="guest-list-checkin-status guest-list-checked-in">
                      <RsvpIcon />
                      <span>Accepted</span>
                    </button>
                  )}
                  {guest.isCheckedIn && (
                    <span className="guest-list-checkin-status guest-list-checked-in">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"
                          fill="currentColor"
                        />
                      </svg>
                      Checked In
                    </span>
                  )}
                </div>
                {!isUsher && (
                  <div className="guest-list-status">
                    {guest.eventAttire?.paid && (
                      <span className="guest-list-badge guest-list-attire">
                        Attire
                      </span>
                    )}
                    {guest.ticket !== "Nil" && (
                      <span className="guest-list-badge guest-list-ticket">
                        Ticket
                      </span>
                    )}
                    {guest.cashGiftAmount > 0 && (
                      <span className="guest-list-badge guest-list-cash-gift">
                        Cash Gift
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      <div className="guest-list-pagination">
        <div className="guest-list-rows-per-page">
          {/* <span>Rows per page</span> */}
          <div className="guest-list-select-wrapper">
            <select value={rowsPerPage} onChange={handleRowsPerPageChange}>
              <option value={1}>1</option>
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
            </select>
            <DropdownArrowIcon />
          </div>
        </div>
        <div className="guest-list-page-info">
          <span>
            {indexOfFirstGuest + 1}-
            {Math.min(indexOfLastGuest, filteredGuests.length)} of{" "}
            {filteredGuests.length}
          </span>
        </div>
        <div className="guest-list-page-navigation">
          <button
            className="guest-list-prev-btn"
            onClick={() => handlePageChange("prev")}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <button
            className="guest-list-next-btn"
            onClick={() => handlePageChange("next")}
            disabled={indexOfLastGuest >= filteredGuests.length}
          >
            Next
          </button>
        </div>
      </div>

      {selectedGuest && isModalOpen && (
        <GuestDetailsModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedGuest(null);
          }}
          guestDetails={selectedGuest}
          paymentDetails={selectedGuest.paymentDetails}
          onDecline={handleDecline}
          onApprove={handleApprove}
          hideRefundActions={false}
          showRSVP={true}
          userRole={userRole}
          isHistoryTab={false}
        />
      )}
    </div>
  );
};

export default GuestList;
